import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Label,
  PlusIcon,
  Select,
  SelectSearch,
  Text,
} from '@mercai/clever'
import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import { useSupplierList, useSupplierWarehouseList } from '@/services'
import {
  SupplierForecastAgreementEnum,
  SupplierProductPackageEnum,
  SupplierProps,
} from '@/types'

import { SupplierForecastFormProps } from './supplier-forecast-form.types'

import styles from './supplier-forecast-form.module.css'

export const SupplierForecastForm = ({
  values,
  productItem,
  supplierUuid,
  errors,
  onChangeValues,
  openNewWarehouse,
}: SupplierForecastFormProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentsSupplierForecastForm')

  const [supplierSearch, setSupplierSearch] = useState('')
  const [supplier, setSupplier] = useState<Partial<SupplierProps>>({
    uuid: supplierUuid || values?.supplierProduct?.supplier?.uuid,
  })

  const product = values?.supplierProduct?.catalogProduct || productItem

  const onHandleSupplierProductValues = (
    key: string,
    val: string | number | boolean | null | undefined,
  ) => {
    onChangeValues({
      ...values,
      supplierProduct: {
        ...values.supplierProduct,
        [key]: val,
      },
    })
  }

  const onHandleValues = (
    key: string,
    val: string | number | boolean | null | undefined,
  ) => {
    onChangeValues({ ...values, [key]: val })
  }

  const { data: dataSuppliers, isLoading: isLoadingSuppliers } =
    useSupplierList({
      isEnable: true,
      params: {
        search: supplierSearch,
      },
    })

  const { data: warehouses } = useSupplierWarehouseList({
    params: {
      supplier__uuid: supplier?.uuid || supplierUuid || '',
    },
  })

  const onSelectSupplier = (supplier: Partial<SupplierProps>) => {
    setSupplier({
      ...supplier,
    })

    onChangeValues({
      ...values,
      supplierProduct: {
        ...values.supplierProduct,
        supplierUuid: supplier?.uuid,
      },
    })
  }

  const CostInput = useCallback(
    ({
      cost,
      errors,
      onChange,
    }: {
      cost?: number
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      errors: any
      onChange: (key: string, value: string | number) => void
    }) => (
      <div>
        <Label>{t('supplierProductsFormPriceLabel')}</Label>
        <InputNumber
          showIcons
          placeholder={`${t('supplierProductsFormPricePlaceholder')} ${values?.currency || 'USD'}`}
          value={cost}
          onChange={(newValue) => onChange('cost', newValue)}
          error={errors?.cost as string}
          decimalPrecision={DECIMAL_PLACES_DEFAULT}
        />
      </div>
    ),
    [values?.currency],
  )

  useEffect(() => {
    if (product) {
      onHandleSupplierProductValues('catalogProductUuid', product?.uuid || '')
    }
  }, [product])

  useEffect(() => {
    if (values?.supplierProduct?.supplier) {
      setSupplier({
        ...values?.supplierProduct?.supplier,
      })
    }
  }, [values?.supplierProduct?.supplier])

  return (
    <div>
      {product && (
        <div className={styles.product}>
          <div>
            <Text variant="text-base">
              <strong>{product?.attributes?.title}</strong>
            </Text>
          </div>
          <div className={styles.row}>
            <Text>
              <strong>{t('newSupplierProductsFormCategoryLabel')}</strong>{' '}
              {product?.category?.name}
            </Text>
          </div>

          {product?.reference && (
            <div className={styles.row}>
              <Text>
                <strong>{t('newSupplierProductsFormReferenceLabel')}</strong>{' '}
                {product.reference}
              </Text>
            </div>
          )}

          {product?.attributes?.fluidity && (
            <div className={styles.row}>
              <Text>
                <strong>{t('newSupplierProductsFormFluidityLabel')}</strong>{' '}
                {product.attributes.fluidity} g/10m
              </Text>
            </div>
          )}

          {product?.attributes?.density && (
            <div className={styles.row}>
              <Text>
                <strong>{t('newSupplierProductsFormDensityLabel')}</strong>{' '}
                {product.attributes.density} g/cm³
              </Text>
            </div>
          )}
        </div>
      )}

      <div className={styles.content}>
        <div className={styles['content-form']}>
          {!supplierUuid && (
            <div>
              <Label required>{t('supplierProductFormSupplierLabel')}</Label>
              <SelectSearch<SupplierProps>
                hasLabel={false}
                dataSearch={dataSuppliers?.data?.results || []}
                isLoadingSearch={isLoadingSuppliers}
                onSearch={setSupplierSearch}
                onSelectItem={onSelectSupplier}
                selectedItem={supplier as SupplierProps}
                keyLabel="name"
                keyValue="uuid"
                translations={{
                  inputSearchPlaceholder: t(
                    'supplierProductFormSupplierPlaceholder',
                  ),
                  noResults: t('supplierProductFormSupplierNoResults'),
                }}
                error={
                  (errors?.supplier?.uuid || errors?.supplierUuid) as string
                }
              />
            </div>
          )}

          <Text variant="title-medium" className={styles['title-form']}>
            {t('supplierProductsFormDescription')}
          </Text>

          <div>
            <Label>{t('supplierProductsFormCurrencyLabel')}</Label>
            <Select
              options={[
                { label: 'MXN', value: 'MXN' },
                { label: 'USD', value: 'USD' },
              ]}
              value={values?.currency || 'USD'}
              onChange={(newValue) => onHandleValues('currency', newValue)}
              error={errors?.currency as string}
            />
          </div>

          <CostInput
            cost={values?.cost}
            errors={errors?.cost}
            onChange={onHandleValues}
          />

          <div>
            <Label>{t('supplierProductsFormCostExpirationDateLabel')}</Label>
            <DatePicker
              placeholder={'DD/MM/AAAA'}
              value={values?.expiresAt}
              onChange={(newValue) => onHandleValues('expiresAt', newValue)}
              error={errors?.costExpirationDate as string}
            />
          </div>

          <div>
            <Label>{t('supplierProductsFormAgreementLabel')}</Label>
            <Select
              options={[
                {
                  label: t('supplierProductsFormAgreementEmptyLabel'),
                  value: '',
                },
                ...Object.keys(SupplierForecastAgreementEnum).map((key) => ({
                  label: tEnums(`forecast_agreement_${key}`),
                  value: key,
                })),
              ]}
              placeholder={t('supplierProductsFormAgreementPlaceholder')}
              value={values?.agreementType}
              onChange={(newValue) => onHandleValues('agreementType', newValue)}
              error={errors?.agreementType as string}
            />
          </div>

          <div>
            <Label>{t('supplierProductsFormAgreementReferenceLabel')}</Label>
            <Input
              value={values?.agreementReference}
              onChange={(newValue) =>
                onHandleValues('agreementReference', newValue)
              }
              error={errors?.forecasts?.[0]?.agreementReference as string}
            />
          </div>

          <div>
            <Label>{t('supplierProductsFormQuantityLabel')}</Label>
            <InputNumber
              showIcons
              placeholder={t('supplierProductsFormAvailablePlaceholder')}
              value={values?.quantity}
              onChange={(newValue) => onHandleValues('quantity', newValue)}
              error={errors?.quantity as string}
              suffix=" Kg"
              decimalPrecision={0}
            />
          </div>

          <div>
            <Label>{t('supplierProductsFormPackingLabel')}</Label>
            <Select
              options={Object.keys(SupplierProductPackageEnum).map((key) => ({
                label: tEnums(`supplier_product_package_${key}`),
                value: key,
              }))}
              placeholder={t('supplierProductsFormPackingPlaceholder')}
              value={values?.supplierProduct?.package}
              onChange={(newValue) =>
                onHandleSupplierProductValues('package', newValue)
              }
              error={errors?.package as string}
            />
          </div>

          <div>
            <Label>{t('supplierProductsFormMinLabel')}</Label>
            <InputNumber
              showIcons
              placeholder={t('supplierProductsFormMinPlaceholder')}
              value={values?.supplierProduct?.minimumPurchaseQuantity}
              onChange={(newValue) =>
                onHandleSupplierProductValues(
                  'minimumPurchaseQuantity',
                  newValue,
                )
              }
              error={errors?.minimumPurchaseQuantity as string}
              suffix=" Kg"
              decimalPrecision={0}
            />
          </div>

          <div>
            <Label>{t('supplierProductsFormMinimumDeliveryTimeLabel')}</Label>
            <InputNumber
              showIcons
              placeholder={t(
                'supplierProductsFormMinimumDeliveryTimePlaceholder',
              )}
              value={values?.supplierProduct?.minDeliveryDays}
              onChange={(newValue) =>
                onHandleSupplierProductValues('minDeliveryDays', newValue)
              }
              error={errors?.minDeliveryDays as string}
              suffix={t('supplierProductsFormMinimumDeliveryTimeSuffix')}
              decimalPrecision={0}
            />
          </div>

          <div>
            <Label>{t('supplierProductsFormWarehouseLabel')}</Label>
            <div className={styles['warehouse-select']}>
              <Select
                options={
                  warehouses?.data?.results?.map?.((warehouse) => ({
                    label: `${warehouse.name}`,
                    value: warehouse?.uuid || '',
                  })) || []
                }
                placeholder={t('supplierProductsFormWarehousePlaceholder')}
                value={values?.supplierProduct?.supplierWarehouseUuid}
                onChange={(newValue) =>
                  onHandleSupplierProductValues(
                    'supplierWarehouseUuid',
                    newValue,
                  )
                }
                error={errors?.supplierWarehouseUuid as string}
              />
              {openNewWarehouse && (
                <Button
                  variant="secondary"
                  model="square"
                  onClick={openNewWarehouse}
                >
                  <PlusIcon />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
